import { M } from 'components/mobile/theme';

/** Guarded wrapper over window.Telegram.WebApp — every helper is a cheap no-op outside Telegram. */

/** The (unverified, display-only) Telegram user. Never trust for identity. */
export interface TelegramWebAppUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  photo_url?: string;
}

export interface TelegramWebApp {
  /** Raw signed handshake string — the API verifies it server-side. */
  initData: string;
  initDataUnsafe: {
    user?: TelegramWebAppUser;
    start_param?: string;
  };
  version: string;
  platform: string;
  colorScheme: 'light' | 'dark';
  ready(): void;
  expand(): void;
  setHeaderColor(color: string): void;
  setBackgroundColor(color: string): void;
  /** Newer clients only — absent on old versions. */
  disableVerticalSwipes?(): void;
  /** Native back chevron in the Telegram header. */
  BackButton?: {
    show(): void;
    hide(): void;
    onClick(cb: () => void): void;
    offClick(cb: () => void): void;
  };
  HapticFeedback?: {
    impactOccurred(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'): void;
  };
}

declare global {
  interface Window {
    Telegram?: {
      WebApp?: TelegramWebApp;
    };
  }
}

export function getWebApp(): TelegramWebApp | undefined {
  return typeof window !== 'undefined' ? window.Telegram?.WebApp : undefined;
}

/** True only inside a real Telegram context (a signed initData is present). */
export function isTelegram(): boolean {
  const wa = getWebApp();
  return !!wa && typeof wa.initData === 'string' && wa.initData.length > 0;
}

/** The raw signed initData string sent to the API for verification. */
export function getInitData(): string {
  return getWebApp()?.initData ?? '';
}

/** The (unverified, display-only) Telegram user. Never trust for identity. */
export function getUnsafeUser(): TelegramWebAppUser | undefined {
  return getWebApp()?.initDataUnsafe?.user;
}

/** Deep-link payload from t.me/<bot>/app?startapp=... (class/test code). */
export function getStartParam(): string | undefined {
  const raw = getWebApp()?.initDataUnsafe?.start_param;
  return raw && raw.trim() ? raw.trim() : undefined;
}

function safe(fn: () => void): void {
  try {
    fn();
  } catch {
    // Best-effort: never let an optional bridge call crash the app.
  }
}

/** Boot: signal ready, expand to full height, pin chrome colors to `M.bg`. No-op outside Telegram. */
export function initTelegram(): void {
  const wa = getWebApp();
  if (!wa) return;
  safe(() => wa.ready());
  safe(() => wa.expand());
  safe(() => wa.setHeaderColor(M.bg));
  safe(() => wa.setBackgroundColor(M.bg));
  // Stop a downward swipe from collapsing/closing the app mid-scroll (newer
  // clients only). No-op where unsupported.
  safe(() => wa.disableVerticalSwipes?.());
}

/** `onBack` is kept in a ref so the handler registers once and never goes stale. No-op outside Telegram. */
export function showBackButton(onBack: () => void): () => void {
  const bb = getWebApp()?.BackButton;
  if (!bb) return () => undefined;
  const handler = () => {
    safe(() => getWebApp()?.HapticFeedback?.impactOccurred('light'));
    onBack();
  };
  safe(() => bb.onClick(handler));
  safe(() => bb.show());
  return () => {
    safe(() => bb.offClick(handler));
    safe(() => bb.hide());
  };
}
